/**
 * Pricing page — shows the available subscription tiers for Zeytune.
 * Accessible via the "Pricing" link in the landing page nav bar.
 *
 * The Pro tier opens a Lemon Squeezy checkout session when the user clicks
 * "Subscribe". Users already on Pro see a "Current plan" indicator instead.
 */
import React, { useState } from 'react';
import { INSIGHT_LIMIT, resolveEffectiveTier } from '../../pricing';
import { useStore } from '../../store/useStore';
// createCheckoutSession temporarily unused while Pro is disabled

type PricingPageProps = {
    onBack: () => void;
};

/** Data for each pricing tier displayed on the pricing page. */
const TIERS = [
    {
        id: 'free',
        name: 'Free',
        price: '€0',
        period: 'forever',
        description: 'Get started with the core features of Zeytune at no cost.',
        features: [
            '2 Projects',
            `${INSIGHT_LIMIT} Insights per Project`,
            'Private & Secure',
        ],
        cta: 'Get started',
        highlighted: false,
    },
    {
        id: 'pro',
        name: 'Pro',
        price: 'TBD',
        period: 'per month',
        description: 'For individuals who need the full power of Zeytune.',
        features: [
            '20 Projects + Import / Export',
            '1000 (and increasing!) Insights',
            'AI based insights consolidation and slicing for opportunities and stories',
            'All new upcoming awesomeness',
        ],
        cta: 'Subscribe',
        highlighted: true,
    },
    {
        id: 'team',
        name: 'Team',
        price: '€TBD',
        period: 'per seat / month',
        description: 'Built for product teams that need shared access and collaboration.',
        features: [
            'Unlimited Projects and Insights',
            'Customized Solutions',
            'More AI power!',
            'All new upcoming awesomeness',
        ],
        cta: 'Coming soon',
        highlighted: false,
    },
];

export default function PricingPage({ onBack }: PricingPageProps) {
    const user = useStore((s) => s.user);
    const [checkoutLoading] = useState(false);
    const [checkoutError] = useState<string | null>(null);

    // Use the effective tier so power_user/zeytune_team see their actual access level,
    // not just their stored billing plan.
    const currentPlan = resolveEffectiveTier(user);
    const isAuthenticated = !!user;

    const handleGetStarted = () => {
        onBack();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Pro checkout is currently disabled; handler removed to avoid unused-var lint

    /**
     * Resolves the CTA button label and handler for a given tier,
     * taking into account the user's current plan and auth state.
     */
    function resolveTierCta(tier: typeof TIERS[number]) {
        // Only show "Current plan" for authenticated users
        const isCurrentPlan = isAuthenticated && tier.id === currentPlan;

        if (isCurrentPlan) {
            return { label: 'Current plan', disabled: true, onClick: undefined };
        }

        if (tier.id === 'free') {
            return { label: 'Get started', disabled: false, onClick: handleGetStarted };
        }

        if (tier.id === 'pro') {
            // Temporarily hide the Pro pricing and disable subscriptions
            return {
                label: checkoutLoading ? 'Loading…' : 'Coming soon',
                disabled: true,
                onClick: undefined,
            };
        }

        // Teams tier — not yet available
        return { label: 'Coming soon', disabled: true, onClick: undefined };
    }

    return (
        <div className='info-page'>
            <div className='info-page-inner info-page-inner--wide'>
                <button onClick={onBack} className='policy-back-btn'>
                    ← Back
                </button>

                <h1 className='info-page-display-title'>Pricing</h1>

                <p className='info-page-lead'>
                    Simple, transparent pricing. Start for free now to evaluate, and upgrade when you're ready.
                </p>

                {checkoutError && (
                    <p className='pricing-checkout-error' role='alert'>
                        {checkoutError}
                    </p>
                )}

                {/* Three-tier grid */}
                <div className='pricing-tiers'>
                    {TIERS.map((tier) => {
                        const cta = resolveTierCta(tier);
                        return (
                            <div
                                key={tier.id}
                                className={`pricing-tier${tier.highlighted ? ' pricing-tier--highlighted' : ''}`}
                            >
                                <div className='pricing-tier-header'>
                                    <h2 className='pricing-tier-name'>{tier.name}</h2>
                                    <div className='pricing-tier-price'>
                                        <span className='pricing-tier-amount'>{tier.price}</span>
                                        <span className='pricing-tier-period'>{tier.period}</span>
                                    </div>
                                    <p className='pricing-tier-desc'>{tier.description}</p>
                                </div>

                                <ul className='pricing-tier-features'>
                                    {tier.features.map((feature, i) => (
                                        <li key={i} className='pricing-tier-feature-item'>
                                            <span className='pricing-tier-check'>✓</span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    className={`pricing-tier-cta${tier.highlighted ? ' pricing-tier-cta--primary' : ''}`}
                                    disabled={cta.disabled}
                                    onClick={cta.onClick}
                                >
                                    {cta.label}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
